/* verqo — LIVE pricing page at "/pricing" · Jun 2026
   Pricing section moved off the homepage into a standalone sub-page,
   wrapped in the shared mega-menu header + minimal footer.
*/
import { useState } from "react";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { SubPageLayout, Reveal, Eyebrow, CTABanner, FAQ } from "../components/marketing/SubPageBlocks";
import PageSEO from "../components/marketing/PageSEO";

const PRICING_PLANS = [
  {
    id: "starter",
    name: "Starter",
    sub: "For founder-led brands just starting wholesale",
    monthlyPrice: 99,
    annualPrice: 89,
    buyersIncluded: 2,
    overageRate: 30,
    featured: false,
    eyebrow: null,
    heading: "Everything unlocked",
    bullets: [
      { bold: "2 active buyers included", text: " · then $30/buyer/mo" },
      { bold: "Custom domain", text: " on day 1" },
      { bold: "White-label", text: " buyer experience" },
      { text: "Unlimited orders · unlimited catalog" },
      { bold: "1 team seat (owner only)" },
      { text: "Payments · Credit · Shipping · Insurance — full access" },
      { text: "App Store access" },
      { text: "Webhooks + REST API · 60 req/min" },
      { text: "6-month data retention" },
      { text: "Self-serve onboarding" },
    ],
  },
  {
    id: "growth",
    name: "Growth",
    sub: "For wholesale brands with a team running one brand",
    monthlyPrice: 599,
    annualPrice: 539,
    buyersIncluded: 10,
    overageRate: 25,
    featured: true,
    eyebrow: "Most popular",
    heading: "Everything in Starter, plus",
    bullets: [
      { bold: "10 active buyers included", text: " · then $25/buyer/mo" },
      { bold: "5 team seats" },
      { bold: "Remove 'Powered by verqo'", text: " branding" },
      { text: "Email support (biz hours)" },
      { text: "API at 600 req/min" },
      { text: "24-month data retention" },
      { text: "Custom invoice & email templates" },
      { text: "Full CSS customisation of buyer portal" },
      { text: "1 onboarding call included" },
      { text: "Beta feature access" },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    sub: "For scaled wholesale operators with multi-channel ops",
    monthlyPrice: 1499,
    annualPrice: 1349,
    buyersIncluded: 50,
    overageRate: 20,
    featured: false,
    eyebrow: null,
    heading: "Everything in Growth, plus",
    bullets: [
      { bold: "50 active buyers included", text: " · then $20/buyer/mo" },
      { bold: "20 team seats" },
      { bold: "Multi-warehouse + multi-region" },
      { text: "Advanced reporting + custom dashboards" },
      { text: "API at 2,400 req/min" },
      { text: "48-month data retention" },
      { text: "Dedicated onboarding specialist" },
      { text: "Priority email + chat support" },
      { text: "Custom integrations available" },
      { text: "Volume pricing on transaction fees" },
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    sub: "For multi-brand or multi-warehouse operations",
    monthlyPrice: 3999,
    annualPrice: 3599,
    buyersIncluded: 100,
    overageRate: 15,
    featured: false,
    eyebrow: null,
    heading: "Everything in Pro, plus",
    bullets: [
      { bold: "100 active buyers included", text: " · then $15/buyer/mo" },
      { bold: "Unlimited team seats" },
      { bold: "Unlimited warehouses" },
      { bold: "Unlimited brand entities" },
      { text: "Unlimited catalogs · unlimited data retention" },
      { text: "API at 10,000 req/min" },
      { bold: "Live chat + phone support", text: " (24/7)" },
      { bold: "Dedicated Customer Success Manager" },
      { text: "3 onboarding calls included" },
      { bold: "SSO + custom contract pricing" },
      { text: "Multi-currency · multi-language buyer portal" },
      { text: "Advanced analytics + AI demand forecasting" },
    ],
  },
];

const TXN_ROWS = [
  { service: "Wholesale invoicing",       starter: "FREE",     growth: "FREE",     pro: "FREE",     enterprise: "FREE",     note: "No commission per invoice" },
  { service: "ACH · standard (next-day)", starter: "$0.50/tx", growth: "$0.50/tx", pro: "$0.50/tx", enterprise: "$0.50/tx", note: "Same flat fee on every plan" },
  { service: "ACH · same-day",            starter: "$3.50/tx", growth: "$3.50/tx", pro: "$3.50/tx", enterprise: "$3.50/tx", note: "Premium speed — posts within hours" },
  { service: "Wire transfer · domestic",  starter: "$15/tx",   growth: "$15/tx",   pro: "$15/tx",   enterprise: "$15/tx",   note: "Industry-standard wire fee" },
  { service: "Instant Payment Request",   starter: "$5/tx",    growth: "$5/tx",    pro: "$5/tx",    enterprise: "$5/tx",    note: "Real-time RFP — funds within 30 min" },
  { service: "Instant payout",            starter: "0.50%",    growth: "0.50%",    pro: "0.30%",    enterprise: "0.30%",    note: "Get paid in seconds vs Net-30/60/90", highlight: true },
  { service: "Credit card",               starter: "4%",       growth: "3%",       pro: "3%",       enterprise: "3%",       note: "Card-acceptance fee" },
];

const FAQ_ITEMS = [
  { q: "Do buyers pay to use verqo?",       a: "No. Buyers use verqo for free — always. Sellers pay to run the wholesale OS." },
  { q: "What counts as an active buyer?",   a: "Any buyer account that places or receives an order in a given billing month. Dormant accounts cost nothing." },
  { q: "Can I switch plans anytime?",       a: "Yes. Upgrade or downgrade any month. Annual plans convert on renewal, and you keep the 10% discount." },
  { q: "Are there per-invoice commissions?", a: "Never. Unlike marketplaces, verqo takes no cut of your GMV. You pay a flat subscription plus the transaction rails you actually use." },
  { q: "Is there an onboarding fee?",       a: "No. Growth includes one onboarding call, Pro includes a dedicated specialist, Enterprise includes three calls plus a CSM." },
];

export default function PricingPage() {
  const [billing, setBilling] = useState("monthly");

  return (
    <SubPageLayout modeOverride="seller">
      <PageSEO
        title="Pricing · verqo"
        description="One platform, four simple plans. Starter $99/mo, Growth $599/mo, Pro $1,499/mo, Enterprise $3,999/mo. No GMV cut, no per-invoice commissions. Buyers use verqo free, forever."
        path="/pricing"
      />

      {/* Hero */}
      <section className="relative pt-20 md:pt-28 pb-6 px-6 md:px-12 max-w-[1280px] mx-auto text-center" data-testid="pricing-page-hero">
        <Reveal><Eyebrow>Pricing</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-semibold text-slate-900">
            One platform. <span className="text-slate-400 italic">Four simple plans.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl mx-auto text-sm md:text-base leading-relaxed text-slate-600">
            Built for wholesale brands moving real volume — beauty, food, coffee, pet, wellness.
            Easy entrance. Pay only for the apps and services you use. <strong className="text-slate-900">Buyers are free, forever.</strong>
          </p>
        </Reveal>

        <Reveal delay={0.22}>
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="pricing-billing-toggle">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              data-testid="billing-monthly"
              className={`px-4 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
                billing === "monthly" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              data-testid="billing-annual"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
                billing === "annual" ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Annual
              <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full ${
                billing === "annual" ? "bg-lime-300 text-slate-900" : "bg-lime-100 text-lime-800"
              }`}>Save 10%</span>
            </button>
          </div>
        </Reveal>
      </section>

      {/* Plan cards */}
      <section className="relative pb-10 px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-[1300px] mx-auto" data-testid="pricing-tiers">
          {PRICING_PLANS.map((t, i) => {
            const shownPrice = billing === "annual" ? t.annualPrice : t.monthlyPrice;
            return (
              <Reveal key={t.id} delay={i * 0.07}>
                <div
                  data-testid={`pricing-card-${t.id}`}
                  className={`h-full rounded-2xl p-7 md:p-8 flex flex-col ${
                    t.featured
                      ? "border-2 border-[#0c1612] bg-white shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)] -translate-y-2"
                      : "border border-slate-200 bg-white"
                  }`}
                >
                  {t.eyebrow && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold w-fit mb-4 bg-lime-300 text-slate-900">
                      <Star className="w-3 h-3" /> {t.eyebrow}
                    </div>
                  )}
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{t.name}</div>
                  <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{t.sub}</div>

                  <div className="mt-6 flex items-baseline gap-1.5">
                    <span className="text-[44px] font-semibold tracking-tight text-slate-900 leading-none">${shownPrice}</span>
                    <span className="text-[13px] text-slate-500">/ month</span>
                  </div>
                  <div className="mt-2 text-[12px] text-slate-500">
                    {billing === "annual" ? "Billed annually · save 10% · cancel anytime" : "Billed monthly · cancel anytime"}
                  </div>
                  {t.buyersIncluded && (
                    <div className="mt-3 text-[12.5px] text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid={`buyer-pricing-${t.id}`}>
                      <strong>{t.buyersIncluded} active buyers</strong> included
                      <span className="text-slate-500"> · then <strong className="text-slate-700">${t.overageRate}</strong>/buyer/mo</span>
                    </div>
                  )}

                  <a
                    href="/waitlist?as=seller"
                    data-testid={`pricing-cta-${t.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                      t.featured ? "bg-[#0c1612] text-white hover:opacity-90" : "bg-white border border-slate-300 text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    Join waitlist
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>

                  <div className="mt-7 pt-6 border-t border-slate-200">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">{t.heading}</div>
                    <ul className="space-y-2.5 flex-1">
                      {t.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                          <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 mt-0.5 flex-shrink-0" />
                          <span>
                            {b.bold && <strong className="text-slate-900">{b.bold}</strong>}
                            {b.text || ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Transaction pricing */}
      <section className="relative py-16 md:py-24 px-6 md:px-12" data-testid="pricing-txn-table">
        <Reveal>
          <div className="max-w-[1100px] mx-auto">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">Transaction pricing</h2>
              <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
                No per-invoice commissions like the marketplaces. Pay only for the rails you use — and lower as you grow.
              </p>
            </div>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-white overflow-x-auto shadow-[0_24px_64px_-32px_rgba(15,23,42,0.18)]">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.5fr] border-b border-slate-200 bg-slate-50/60">
                  <div className="px-5 py-4 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Service</div>
                  <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Starter</div>
                  <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 bg-lime-50/60">Growth</div>
                  <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Pro</div>
                  <div className="px-4 py-4 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Enterprise</div>
                  <div className="px-4 py-4 text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Notes</div>
                </div>
                {TXN_ROWS.map((r, i) => (
                  <div
                    key={r.service}
                    className={`grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.5fr] ${i < TXN_ROWS.length - 1 ? "border-b border-slate-100" : ""} ${r.highlight ? "bg-lime-50/40" : ""}`}
                    data-testid={`txn-row-${i}`}
                  >
                    <div className={`px-5 py-4 text-[13px] font-semibold ${r.highlight ? "text-slate-900" : "text-slate-800"}`}>{r.service}</div>
                    <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.starter}</div>
                    <div className="px-4 py-4 text-center text-[13px] text-slate-900 font-semibold bg-lime-50/30">{r.growth}</div>
                    <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.pro}</div>
                    <div className="px-4 py-4 text-center text-[13px] text-slate-700">{r.enterprise}</div>
                    <div className="px-4 py-4 text-[12px] text-slate-500 leading-snug">{r.note}</div>
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-6 text-center text-[13px] text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Shipping, trade-credit insurance and financing (Net-30 / 60 / 90) pricing is built into how we deliver each service — no surprise line items.
              <br />
              <span className="text-slate-900 font-semibold">Lower transaction fees alone often pay for your Pro or Enterprise upgrade many times over.</span>
            </p>
          </div>
        </Reveal>
      </section>

      <FAQ items={FAQ_ITEMS} />

      <CTABanner
        title="Ready to see it running on your catalog?"
        subtitle="Book a 20-minute demo. We'll import your buyer list and show you your live DSO impact."
        primary={{ label: "Get started", href: "/waitlist?as=seller" }}
        secondary={{ label: "See how it works", href: "/company/how-it-works" }}
      />
    </SubPageLayout>
  );
}
